import type {
  NamedRef,
  PositionedVehicle,
  PlaybackAlert,
  PlaybackPoint,
  PlaybackRange,
  SensorReading,
  TrackedVehicle,
  VehicleStatus,
} from "../domain";
import { DEMO_ALERTS_VEHICLE_ID, demoAlertsBetween } from "./demoAlerts";

const now = new Date().toISOString();

// Sensor readings in the live docs' shape (one per probe).
function cargoTemperature(celsius: number, humidityPercent: number): SensorReading[] {
  return [
    {
      sensorId: "sensor-temperature",
      sensorType: "TEMPERATURE",
      probe: "io:72",
      label: "Cargo temperature",
      quantity: "TEMPERATURE",
      value: celsius,
      unit: "°C",
      stateLabel: null,
    },
    {
      sensorId: "sensor-temperature",
      sensorType: "TEMPERATURE",
      probe: "io:73",
      label: null,
      quantity: "HUMIDITY",
      value: humidityPercent,
      unit: "%",
      stateLabel: null,
    },
  ];
}

function load(kg: number): SensorReading {
  return {
    sensorId: "sensor-weight",
    sensorType: "WEIGHT",
    probe: "io:75",
    label: "Load",
    quantity: "WEIGHT",
    value: kg,
    unit: "kg",
    stateLabel: null,
  };
}

function cargoDoor(open: boolean): SensorReading {
  return {
    sensorId: "sensor-door",
    sensorType: "DOOR_STATUS",
    probe: "io:80",
    label: "Cargo door",
    quantity: "STATE",
    value: open ? 1 : 0,
    unit: null,
    stateLabel: open ? { en: "Open", ar: "مفتوح" } : { en: "Closed", ar: "مغلق" },
  };
}

export const ACME: NamedRef = {
  id: "account-acme",
  nameEn: "Acme Logistics",
  nameAr: "أكمي للخدمات اللوجستية",
};
export const NOOR: NamedRef = {
  id: "account-noor",
  nameEn: "Al Noor Transport",
  nameAr: "النور للنقل",
};

/** The companies a Level 1 user can track, as `GET /tracking/accounts`. */
export const trackingAccounts: readonly NamedRef[] = [ACME, NOOR];

const MAIN_BRANCH: NamedRef = { id: "branch-main", nameEn: "Main branch", nameAr: "الفرع الرئيسي" };
const VAN_TYPE: NamedRef = { id: "type-van", nameEn: "Van", nameAr: "شاحنة صغيرة" };
const TRUCK_TYPE: NamedRef = { id: "type-truck", nameEn: "Truck", nameAr: "شاحنة" };
const CAR_TYPE: NamedRef = { id: "type-car", nameEn: "Car", nameAr: "سيارة" };

export const trackingVehicles: readonly TrackedVehicle[] = [
  {
    id: "van-17",
    name: "Distribution Van 2",
    nameAr: "شاحنة التوزيع 2",
    code: "VAN-0017",
    plateNumber: "DHW 5678",
    locationLabel: "Al Uyaynah – King Fahd Road",
    assetType: VAN_TYPE,
    account: ACME,
    branch: MAIN_BRANCH,
    imei: "350000000001000",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-van-17",
    cameraVideoTransport: "JT1078",
    driver: {
      id: "driver-van-17",
      name: "Aya Al-Rashidi",
      phone: "+966 55 120 1700",
      email: "aya@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.916,
      longitude: 46.376,
      heading: 28,
      speedKph: 68,
      status: "moving",
      ignitionOn: true,
      odometerKm: 125430,
      sensors: [...cargoTemperature(26, 38), load(2450), cargoDoor(false)],
    },
  },
  {
    id: "truck-04",
    name: "Cold Truck 4",
    nameAr: "شاحنة التبريد 4",
    code: "TRK-0004",
    plateNumber: "RBD 2381",
    locationLabel: "Al Uyaynah",
    assetType: TRUCK_TYPE,
    account: ACME,
    branch: MAIN_BRANCH,
    imei: "350000000001001",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-truck-04",
    cameraVideoTransport: "RTMP",
    driver: {
      id: "driver-truck-04",
      name: "Omar Al-Qahtani",
      phone: "+966 55 220 4100",
      email: "omar@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.909,
      longitude: 46.362,
      heading: 305,
      speedKph: 0,
      status: "idle",
      ignitionOn: true,
      odometerKm: 98432,
      sensors: [...cargoTemperature(5, 42), load(6120)],
    },
  },
  {
    id: "sedan-12",
    name: "Service Car 12",
    nameAr: "Service Car 12",
    code: "CAR-0012",
    plateNumber: "KSA 9012",
    locationLabel: "Bawdah",
    assetType: CAR_TYPE,
    account: NOOR,
    branch: MAIN_BRANCH,
    imei: "350000000001002",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-sedan-12",
    cameraVideoTransport: "JT1078",
    driver: {
      id: "driver-sedan-12",
      name: "Mona Al-Harbi",
      phone: "+966 55 310 1200",
      email: "mona@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.902,
      longitude: 46.386,
      heading: 110,
      speedKph: 0,
      status: "stopped",
      ignitionOn: false,
      odometerKm: 41670,
      sensors: [],
    },
  },
  {
    id: "van-08",
    name: "Distribution Van 8",
    nameAr: "Distribution Van 8",
    code: "VAN-0008",
    plateNumber: "JED 7741",
    locationLabel: "Shuaib Al Haysiyah",
    assetType: VAN_TYPE,
    account: ACME,
    branch: MAIN_BRANCH,
    imei: "350000000001003",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-van-08",
    cameraVideoTransport: "JT1078",
    driver: {
      id: "driver-van-08",
      name: "Fahad Al-Dosari",
      phone: "+966 55 480 0800",
      email: "fahad@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.890,
      longitude: 46.369,
      heading: 185,
      speedKph: 0,
      status: "offline",
      ignitionOn: false,
      odometerKm: 136221,
      sensors: [load(1730)],
    },
  },
  {
    id: "truck-09",
    name: "Delivery Truck 9",
    nameAr: "شاحنة التوصيل 9",
    code: "TRK-0009",
    plateNumber: "RUH 4409",
    locationLabel: "King Abdulaziz Military Academy",
    assetType: TRUCK_TYPE,
    account: ACME,
    branch: MAIN_BRANCH,
    imei: "350000000001004",
    hasDevice: true,
    hasCamera: false,
    cameraDeviceId: null,
    cameraVideoTransport: null,
    driver: {
      id: "driver-truck-09",
      name: "Noura Al-Shammari",
      phone: "+966 55 510 0900",
      email: "noura@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.923,
      longitude: 46.349,
      heading: null,
      speedKph: 43,
      status: "moving",
      ignitionOn: true,
      odometerKm: 77892,
      sensors: [load(5210), cargoDoor(true)],
    },
  },
  {
    id: "car-21",
    name: "Support Car 21",
    nameAr: "سيارة الدعم 21",
    code: "CAR-0021",
    plateNumber: "KSA 2210",
    locationLabel: "Al Ammariyah",
    assetType: CAR_TYPE,
    account: NOOR,
    branch: MAIN_BRANCH,
    imei: "350000000001005",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-car-21",
    cameraVideoTransport: "JT1078",
    driver: {
      id: "driver-car-21",
      name: "Salem Al-Mutairi",
      phone: "+966 55 620 2100",
      email: "salem@example.com",
    },
    supervisor: null,
    telemetry: {
      sequence: 1,
      recordedAt: now,
      gpsTime: now,
      lastCommunicationAt: now,
      latitude: 24.881,
      longitude: 46.398,
      heading: 230,
      speedKph: 0,
      status: "idle",
      ignitionOn: true,
      odometerKm: 23012,
      sensors: [],
    },
  },
  // Listed but not on the map: a device that hasn't reported a position
  // yet, and an asset with no device at all.
  {
    id: "van-30",
    name: "New Van 30",
    nameAr: "شاحنة جديدة 30",
    code: "VAN-0030",
    plateNumber: "",
    locationLabel: "",
    assetType: VAN_TYPE,
    account: ACME,
    branch: MAIN_BRANCH,
    imei: "350000000001006",
    hasDevice: true,
    hasCamera: true,
    cameraDeviceId: "camera-van-30",
    cameraVideoTransport: "JT1078",
    driver: null,
    supervisor: null,
    telemetry: null,
  },
  {
    id: "car-31",
    name: "Pool Car 31",
    nameAr: "سيارة مشتركة 31",
    code: "CAR-0031",
    plateNumber: "RUH 3131",
    locationLabel: "",
    assetType: CAR_TYPE,
    account: ACME,
    branch: null,
    imei: null,
    hasDevice: false,
    hasCamera: false,
    cameraDeviceId: null,
    cameraVideoTransport: null,
    driver: null,
    supervisor: null,
    telemetry: null,
  },
];


const MINUTE_MS = 60_000;
/** Keeps very long ranges to a sane number of mock points. */
const MAX_MOCK_POINTS = 6_000;

/** Repeating activity pattern the mock walks through (minutes per block). */
const MOCK_ACTIVITY: readonly { readonly status: VehicleStatus; readonly minutes: number }[] = [
  { status: "moving", minutes: 42 },
  { status: "idle", minutes: 9 },
  { status: "moving", minutes: 64 },
  { status: "stopped", minutes: 100 },
  { status: "moving", minutes: 35 },
  { status: "idle", minutes: 14 },
  { status: "moving", minutes: 51 },
  { status: "stopped", minutes: 75 },
];

const MOCK_PLACES: readonly string[] = [
  "الرياض – طريق الملك فهد",
  "العيينة – طريق صلبوخ",
  "الرياض – حي العليا",
  "الرياض – حي الملز",
  "الدرعية – طريق الملك خالد",
];

function seedOf(text: string): number {
  return [...text].reduce((seed, char) => (seed * 31 + char.charCodeAt(0)) >>> 0, 7);
}

function bearingDegrees(fromLat: number, fromLng: number, toLat: number, toLng: number) {
  const radians = Math.atan2(toLng - fromLng, toLat - fromLat);
  return ((radians * 180) / Math.PI + 360) % 360;
}

/**
 * Mock history for one asset and range (until the backend endpoint exists):
 * a point per minute (coarser for very long ranges), walking a repeating
 * moving / temporarily stopped / stopped pattern, with its segments derived
 * from the same blocks. Deterministic per vehicle so tests are stable.
 */
export function createPlaybackPoints(
  vehicle: PositionedVehicle,
  range: PlaybackRange,
): PlaybackPoint[] {
  const fromMs = Date.parse(range.from);
  const toMs = Date.parse(range.to);
  if (!(toMs > fromMs)) return [];

  const stepMs = Math.max(MINUTE_MS, Math.ceil((toMs - fromMs) / MAX_MOCK_POINTS / MINUTE_MS) * MINUTE_MS);
  const seed = seedOf(vehicle.id);
  let latitude = vehicle.telemetry.latitude - 0.02;
  let longitude = vehicle.telemetry.longitude - 0.03;
  let heading = seed % 360;
  let odometerKm = (vehicle.telemetry.odometerKm ?? 0) - 120;

  const points: PlaybackPoint[] = [];
  let blockIndex = seed % MOCK_ACTIVITY.length;
  let blockStart = fromMs;

  while (blockStart < toMs) {
    const block = MOCK_ACTIVITY[blockIndex % MOCK_ACTIVITY.length]!;
    const blockEnd = Math.min(toMs, blockStart + block.minutes * MINUTE_MS);
    const placeStart = MOCK_PLACES[(blockIndex + seed) % MOCK_PLACES.length]!;

    for (let at = blockStart; at < blockEnd || (at === blockEnd && blockEnd === toMs); at += stepMs) {
      const moving = block.status === "moving";
      const speedKph = moving ? 38 + ((at / stepMs + seed) % 9) * 5 : 0;
      if (moving) {
        // Drift the heading a little each step and move along it.
        heading = (heading + (((at / stepMs) % 7) - 3) * 6 + 360) % 360;
        const distanceKm = (speedKph * stepMs) / 3_600_000;
        const nextLatitude = latitude + (distanceKm / 111) * Math.cos((heading * Math.PI) / 180);
        const nextLongitude =
          longitude + (distanceKm / (111 * Math.cos((latitude * Math.PI) / 180))) * Math.sin((heading * Math.PI) / 180);
        heading = bearingDegrees(latitude, longitude, nextLatitude, nextLongitude);
        latitude = nextLatitude;
        longitude = nextLongitude;
        odometerKm += distanceKm;
      }
      points.push({
        sequence: points.length + 1,
        recordedAt: new Date(at).toISOString(),
        // As the playback endpoint sends them.
        gpsTime: new Date(at).toISOString(),
        lastCommunicationAt: null,
        latitude,
        longitude,
        heading,
        speedKph,
        status: block.status,
        ignitionOn: block.status !== "stopped",
        odometerKm: Math.round(odometerKm),
        battery: block.status === "stopped" ? 12.4 : 13.8,
        alerts: NO_ALERTS,
        locationLabel: placeStart,
        // The vehicle's own sensors; a cargo door opens while it's stopped.
        sensors: vehicle.telemetry.sensors.map((reading) =>
          reading.sensorType === "DOOR_STATUS" ? cargoDoor(block.status === "stopped") : reading,
        ),
      });
      if (at === blockEnd) break;
    }

    blockStart = blockEnd;
    blockIndex += 1;
  }

  return vehicle.id === DEMO_ALERTS_VEHICLE_ID ? withDemoAlerts(points, fromMs, toMs) : points;
}

const NO_ALERTS: readonly PlaybackAlert[] = [];

/**
 * The demo vehicle's alerts (demoAlerts.ts), pinned as the backend does: each
 * to the last point recorded at or before it fired (the first point for one
 * before), with no location of their own (playback places them).
 */
function withDemoAlerts(points: PlaybackPoint[], fromMs: number, toMs: number): PlaybackPoint[] {
  if (points.length === 0) return points;
  const times = points.map((point) => Date.parse(point.recordedAt));
  const pinned = new Map<number, PlaybackAlert[]>();
  // The alerts come by time: each one's point is at or after the last's.
  let index = 0;
  for (const alert of demoAlertsBetween(fromMs, toMs)) {
    while (index + 1 < times.length && times[index + 1]! <= alert.occurredMs) index += 1;
    const point = points[index]!;
    const list = pinned.get(index) ?? [];
    list.push({
      id: alert.id,
      occurredAt: alert.occurredAt,
      occurredMs: alert.occurredMs,
      alertType: alert.alertType,
      alertNameEn: alert.alertNameEn,
      alertNameAr: alert.alertNameAr,
      triggeredValueEn: alert.triggeredValueEn,
      triggeredValueAr: alert.triggeredValueAr,
      // As an alert the backend sends without a location: playback draws
      // it on the route where the vehicle was when it fired.
      position: { latitude: point.latitude, longitude: point.longitude },
      hasOwnLocation: false,
    });
    pinned.set(index, list);
  }
  return points.map((point, index) => {
    const alerts = pinned.get(index);
    return alerts === undefined ? point : { ...point, alerts };
  });
}
